// The apps (signup / sign-in) live on separate origins from marketing.
// Set per build via VITE_APP_URL — see .env.development / .env.production (both
// committed). No localhost fallback, so a prod build can never silently use it.
export const APP_URL = import.meta.env.VITE_APP_URL as string;

// Backend API origin, for the public "Book a demo" form. Absolute (marketing has no proxy).
const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';
export const API_BASE = `${API_ORIGIN}/api/v1`;

/** Submit a demo request (unauthenticated public endpoint). Throws with a readable message on failure. */
export async function submitDemoRequest(body: {
  name: string;
  businessName?: string;
  email?: string;
  phone?: string;
  message?: string;
}): Promise<void> {
  const res = await fetch(`${API_BASE}/public/demo-requests`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, source: 'MARKETING' }),
  });
  if (!res.ok) {
    let msg = 'Something went wrong. Please try again.';
    try {
      const j = await res.json();
      if (j?.message) msg = j.message as string;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
}

/** Any "Book a demo" control fires this; the global <DemoModal> listens. */
export function openDemo(): void {
  window.dispatchEvent(new CustomEvent('techsofit:book-demo'));
}

// Gym/staff product (Gym OS). Today this is the same app origin; it will move to
// gym.techsofit.com — point VITE_GYM_URL there when it does.
export const GYM_URL = (import.meta.env.VITE_GYM_URL as string) || APP_URL;

export const PRICING_URL = `${GYM_URL}/pricing`;
/**
 * The single front door. Passwordless email, login == signup; brand-new users pick
 * their intent (individual / coach / gym) in-app. Every app CTA funnels here.
 */
export const START_URL = `${GYM_URL}/account`;
export const SIGNIN_URL = START_URL;
/** "Start free trial" CTAs open the register form directly — everyone gets a free trial,
 * so there's no tier picker first; plan choice happens later in Billing. */
export const SIGNUP_URL = `${GYM_URL}/signup`;
export const GYM_SIGNUP_URL = SIGNUP_URL;
export const TRAINER_SIGNUP_URL = SIGNUP_URL;
/** Gym owners book a demo (falls back to the front door until a dedicated page exists). */
export const DEMO_URL = START_URL;

// Consumer app stores (placeholders until the apps are published).
export const APP_STORE_URL = (import.meta.env.VITE_APP_STORE_URL as string) || '/#app';
export const PLAY_STORE_URL = (import.meta.env.VITE_PLAY_STORE_URL as string) || '/#app';

// The directory is a real route on this site (techsofit.com/discover).
export const DISCOVER_URL = '/discover';
// The App section lives on the homepage; use a root-anchored hash so it works
// from any page (directory pages included).
export const APP_ANCHOR = '/#app';
